import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import Lottie from 'react-lottie';

import { getTranslatedString } from 'helpers/i18n';

import { createNoticeTemplate } from './store/actions';

import successAnimationData from 'assets/success.json';
import CtaPrimary from 'components/ui/CtaPrimary';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import ErrorFace from 'components/ui/AnimatedStates/ErrorFace';
import LoadingFace from 'components/ui/AnimatedStates/LoadingFace';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';

import NoticeTemplateForm from './NoticeTemplateForm/NoticeTemplateForm';

const CREATING_STATES = {
  form: 'form',
  loading: 'loading',
  success: 'success',
  error: 'error',
};

const FORM_NAME = 'create_notice_template_form';

const SuccessFace = ({ handleClose }) => {
  const isMobile = window.matchMedia('(max-width: 500px)').matches;
  const lottieSize = isMobile ? 80 : 150;

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.noticeTemplates.createSuccess')}
      bodyText={getTranslatedString('globalDrawer.successBody')}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};

const CreateNoticeTemplate = ({ handleClose, onSubmissionComplete, createNoticeTemplate }) => {
  const [creatingState, setCreatingState] = useState(CREATING_STATES.form);
  const [tempValues, setTempValues] = useState({});

  const handleCreateNoticeTemplate = useCallback(
    async (values) => {
      setCreatingState(CREATING_STATES.loading);
      try {
        // if the request fails, store the values temporarily to retry
        setTempValues(values);
        await createNoticeTemplate(values);
        setTempValues({});
        setCreatingState(CREATING_STATES.success);
        if (onSubmissionComplete) {
          onSubmissionComplete();
        }
      } catch (err) {
        setCreatingState(CREATING_STATES.error);
      }
    },
    [createNoticeTemplate, onSubmissionComplete]
  );

  const handleRetry = useCallback(() => {
    handleCreateNoticeTemplate(tempValues);
  }, [handleCreateNoticeTemplate, tempValues]);

  if (creatingState === CREATING_STATES.form) {
    return (
      <FormFace handleClose={handleClose} handleCreateNoticeTemplate={handleCreateNoticeTemplate} />
    );
  } else if (creatingState === CREATING_STATES.loading) {
    return (
      <LoadingFace
        headerText={getTranslatedString('landlordPortal.noticeTemplates.createLoading')}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  } else if (creatingState === CREATING_STATES.success) {
    return <SuccessFace handleClose={handleClose} />;
  } else if (creatingState === CREATING_STATES.error) {
    return (
      <ErrorFace
        headerText={getTranslatedString('landlordPortal.noticeTemplates.createError')}
        bodyText={getTranslatedString('globalDrawer.errorBody')}
        onClick={handleClose}
        buttonText={getTranslatedString('close')}
        onSecondaryClick={handleRetry}
        secondaryButtonText={getTranslatedString('globalDrawer.goBack')}
      />
    );
  }
};

const FormFace = ({ handleClose, handleCreateNoticeTemplate }) => {
  return (
    <React.Fragment>
      <DrawerHeader
        onClose={handleClose}
        title={getTranslatedString('landlordPortal.addNoticeTemplate.addNewNoticeTemplate')}
        subHeader={getTranslatedString('landlordPortal.notices.requiredField')}
      />
      <DrawerBody>
        <NoticeTemplateForm
          formName={FORM_NAME}
          formData={{}}
          handleSubmit={handleCreateNoticeTemplate}
        />
      </DrawerBody>
      <DrawerFooter
        primaryActionFormName={FORM_NAME}
        primaryActionText={getTranslatedString('create')}
      />
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({});

const mapDispatchToProps = {
  createNoticeTemplate,
};

export default connect(mapStoreToProps, mapDispatchToProps)(CreateNoticeTemplate);
